import _get from 'lodash/get'
import { AxiosError } from 'axios'
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  useInfiniteQuery,
  UseMutationResult,
  QueryObserverResult,
  UseInfiniteQueryOptions,
  InfiniteQueryObserverResult,
  useMutation,
} from 'react-query'

import http from '../helpers/http'
import { ILifecycleStage, ITimelinesResults } from '../types'
import { filterProspectPatchResponse, getProspect, getProspectProfile } from '../helpers/utils'
import {
  IProspect,
  IRawProspect,
  ProspectsVars,
  ICollectionProspect,
  ICollectionProspectsData,
  ICreateProspect,
} from '../types/prospects-types'

export const prospectProfileKey = 'prospect-profile'
export const collectionProspectKey = 'collection-prospect'
export const collectionProspectsPagedKey = 'collection-prospects-paged'
export const lifecycleStages = 'lifecycle-stages'
export const ProspectsTimelinePagedKey = 'prospect-timeline-paged'
export const prospectTimelineKey = 'prospect-timeline'

export function useProspectProfile(
  id: string,
  configOptions: UseQueryOptions<Partial<IProspect>>
): QueryObserverResult<Partial<IProspect>> {
  const queryFn = ({ signal }: { signal?: AbortSignal }): Promise<Partial<IProspect>> =>
    Promise.all([
      http.get(`/prospects/${id}`, { signal }),
      http.get(`/prospects/metadata`, { signal }),
      http.get(`statistics/deals`, { params: { prospect: id }, signal }),
    ]).then((res) => {
      return {
        ...getProspectProfile({ prospectRawData: _get(res, '[0].data'), prospectMetaData: _get(res, '[1].data') }),
        dealStatistics: _get(res, '[2].data'),
      }
    })
  const config: UseQueryOptions<Partial<IProspect>> = {
    queryKey: [prospectProfileKey, { id }],
    queryFn,
    staleTime: 10000,
    notifyOnChangeProps: 'tracked',
    ...configOptions,
  }
  return useQuery(config)
}

export function useRawProspectById(id: string): QueryObserverResult<Partial<IProspect>> {
  const queryFn = ({ signal }: { signal?: AbortSignal }): Promise<Partial<IProspect>> =>
    http.get(`/prospects/${id}`, { signal }).then((res) => getProspect(res?.data))
  const config: UseQueryOptions<Partial<IProspect>> = {
    queryKey: [prospectProfileKey, { id }],
    queryFn,
    staleTime: 10000,
    notifyOnChangeProps: 'tracked',
  }

  return useQuery(config)
}

export function useCollectionProspectQuery(id: number): QueryObserverResult<ICollectionProspect> {
  const config: UseQueryOptions<ICollectionProspect> = {
    queryKey: [collectionProspectKey, { id }],
    queryFn: ({ signal }): Promise<ICollectionProspect> =>
      http.get(`/collection/prospects/${id}`, { signal }).then((res) => res?.data),
    staleTime: Infinity,
    notifyOnChangeProps: 'tracked',
  }

  return useQuery(config)
}

export function useCollectionProspectsInfiniteQuery(
  { itemsPerPage = 10, ownerId, organizationId, sortName, sortDirection } = {} as ProspectsVars
): InfiniteQueryObserverResult<ICollectionProspectsData> {
  const queryClient = useQueryClient()

  const getNextPageParam = (lastResult: ICollectionProspectsData) => {
    const { data, offset, itemsPerPage } = lastResult
    return data && data.length === itemsPerPage ? { offset: offset + itemsPerPage, itemsPerPage } : undefined
  }

  const config: UseInfiniteQueryOptions<ICollectionProspectsData> = {
    queryKey: [collectionProspectsPagedKey, { ownerId, organizationId, sortName, sortDirection }],
    queryFn: ({ pageParam, signal }) =>
      http
        .get('/collection/prospects', {
          params: {
            offset: pageParam?.offset,
            itemsPerPage,
            'owner.id': ownerId,
            'organization.id': organizationId,
            [`sort[${sortName}]`]: sortDirection,
          },
          signal,
        })
        .then((res) => res?.data),
    getNextPageParam,
    onSuccess(data) {
      for (const page of data?.pages ?? []) {
        for (const prospect of page?.data ?? []) {
          if (prospect) queryClient.setQueryData([collectionProspectKey, { id: prospect.id }], prospect)
        }
      }
    },
  }

  return useInfiniteQuery(config)
}

export function useCollectionProspectsPagedRemoveQueries(): () => void {
  const queryClient = useQueryClient()
  return () => queryClient.removeQueries(collectionProspectsPagedKey)
}

export function useLifecycleStages(): QueryObserverResult<ILifecycleStage[]> {
  const config: UseQueryOptions<ILifecycleStage[]> = {
    queryKey: [lifecycleStages],
    queryFn: ({ signal }): Promise<ILifecycleStage[]> =>
      http.get('/lifecycle-stages', { signal }).then((res) => res?.data),
    staleTime: Infinity,
    notifyOnChangeProps: 'tracked',
  }

  return useQuery(config)
}

export function usePatchProspectProfile(
  id: string
): UseMutationResult<IRawProspect, AxiosError, Partial<IRawProspect>> {
  const queryClient = useQueryClient()
  const mutationFn = (data: Partial<IRawProspect>): Promise<IRawProspect> => {
    return http.patch(`/prospects/${id}`, data).then((res) => res?.data)
  }
  return useMutation(mutationFn, {
    onSuccess(responseData) {
      const cachedData = queryClient.getQueryData([prospectProfileKey, { id }]) as IProspect
      queryClient.setQueryData([prospectProfileKey, { id }], filterProspectPatchResponse({ responseData, cachedData }))
    },
  })
}

export function usePostProspect(): UseMutationResult<ICreateProspect, AxiosError, Partial<ICreateProspect>> {
  const queryClient = useQueryClient()
  const mutationFn = (data: Partial<ICreateProspect>): Promise<ICreateProspect> =>
    http.post(`/prospects`, data).then((res) => res?.data)

  return useMutation(mutationFn, {
    async onSuccess(prospect) {
      queryClient.setQueryData([collectionProspectKey, { id: prospect.id }], prospect)
    },
  })
}

export function useRawProspectByIdFetch(): (id: string) => Promise<Partial<IProspect>> {
  const queryClient = useQueryClient()

  return (id: string) =>
    queryClient.fetchQuery({
      queryKey: [prospectProfileKey, { id }],
      queryFn: ({ signal }): Promise<Partial<IProspect>> =>
        Promise.all([
          http.get(`/prospects/${id}`, { signal }),
          http.get(`/prospects/metadata`, { signal }),
          http.get(`statistics/deals`, { params: { prospect: id }, signal }),
        ]).then((res) => ({
          ...getProspectProfile({
            prospectRawData: _get(res, '0.data', {}),
            prospectMetaData: _get(res, '1.data', {}),
          }),
          dealStatistics: _get(res, '2.data', {}),
        })),
    })
}

export function useProspectTimelineInfiniteQuery(
  { itemsPerPage = 10, id } = {} as { itemsPerPage: number; id: string }
): InfiniteQueryObserverResult<ITimelinesResults> {
  const queryClient = useQueryClient()

  const getNextPageParam = (lastResult: ITimelinesResults) => {
    const { data, offset, itemsPerPage } = lastResult
    return data && data.length === itemsPerPage ? { offset: offset + itemsPerPage, itemsPerPage } : undefined
  }

  const config: UseInfiniteQueryOptions<ITimelinesResults> = {
    queryKey: [ProspectsTimelinePagedKey, { id }],
    queryFn: ({ pageParam, signal }) =>
      http
        .get(`/prospects/${id}/timeline`, {
          params: {
            offset: pageParam?.offset,
            itemsPerPage,
          },
          signal,
        })
        .then((res) => res?.data),
    getNextPageParam,
    onSuccess(data) {
      for (const page of data?.pages ?? []) {
        for (const timeline of page?.data ?? []) {
          if (timeline) queryClient.setQueryData([prospectTimelineKey, { id: timeline.id }], timeline)
        }
      }
    },
  }

  return useInfiniteQuery(config)
}
